import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../useAuth';


// const BalanceApiUrl = 'https://hml-bff.barte.com/service/banking/accounts/balance'
const BalanceApiUrl = `${import.meta.env.VITE_API_BANKING_URL}accounts/balance`;


const DEFAULT_TIMEOUT = 15000;
const DEFAULT_RETRY_ATTEMPTS = 2;

export interface BalanceAccount {
  account_id: string;
  balance: number;
  asset_id: string;
  asset_name: string;
}
export type BalanceResponse = BalanceAccount[];

export interface BalanceInfo {
  currentBalance: number;
  availableBalance: number;
  currency: string;
  lastUpdated: string;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

interface RequestOptions extends RequestInit {
  timeout?: number;
  retryAttempts?: number;
}

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function createTimeoutController(timeout: number) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeout);

  return { controller, timeoutId };
}

async function apiRequest<T>(
  endpoint: string = '',
  options: RequestOptions = {},
  getToken: () => string | null
): Promise<ApiResponse<T>> {
  const { timeout = DEFAULT_TIMEOUT, retryAttempts = DEFAULT_RETRY_ATTEMPTS, ...fetchOptions } = options;

  const token = getToken();
  if (!token) {
    throw new ApiError('ID do vendedor não encontrado. Faça login novamente.', 401);
  }
  const url = new URL(`${BalanceApiUrl}${endpoint}`);
  url.searchParams.append('token', String(token));

  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...fetchOptions.headers,
    },
    ...fetchOptions,
  };

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retryAttempts; attempt++) {
    const { controller, timeoutId } = createTimeoutController(timeout);

    try {
      const response = await fetch(url.toString(), {
        ...defaultOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok) {
        throw new ApiError(
          data.message || `Erro HTTP ${response.status}`,
          response.status,
          data
        );
      }

      return {
        success: true,
        data: data
      };

    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiError) {
        lastError = error;
      } else if (error instanceof Error && error.name === 'AbortError') {
        lastError = new ApiError(
          `Timeout da requisição (${timeout}ms)`,
          408,
          error
        );
      } else {
        lastError = new ApiError(
          'Erro de conectividade',
          0,
          error
        );
      }

      if (attempt < retryAttempts) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}

function processBalanceData(data: any): BalanceInfo {
  if (!data || typeof data.debitAccountAmount === 'undefined') {
    throw new Error('Nenhuma conta encontrada na resposta da API');
  }

  const balanceInReais = data.debitAccountAmount;
  return {
    currentBalance: balanceInReais,
    availableBalance: balanceInReais,
    currency: 'BRL',
    lastUpdated: new Date().toISOString()
  };
}
export async function getBalance(
  options: RequestOptions = {},
  getToken: () => string | null
): Promise<ApiResponse<BalanceInfo>> {
  try {
    const response = await apiRequest < any > ('', options, getToken);

    if (response.success && response.data) {
      const processedBalance = processBalanceData(response.data);
      return {
        success: true,
        data: processedBalance
      };
    }

    return {
      success: false,
      error: 'Falha ao processar dados de saldo'
    };

  } catch (error) {
    error instanceof Error ? error.message : 'Erro desconhecido';
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro desconhecido'
    };
  }
}

export function useBalance(
  autoRefreshInterval: number = 300000,
  requestTimeout: number = DEFAULT_TIMEOUT
) {
  const { getToken } = useAuth();
  const [balance, setBalance] = useState < BalanceInfo | null > (null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState < string | null > (null);
  const [lastUpdated, setLastUpdated] = useState < Date | null > (null);

  const loadBalance = useCallback(async (showLoading: boolean = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const response = await getBalance({ timeout: requestTimeout }, getToken);

      if (response.success && response.data) {
        setBalance(response.data);
        setLastUpdated(new Date());
      } else {
        const errorMessage = response.error || 'Falha ao carregar saldo';
        setError(errorMessage);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [requestTimeout, getToken]);

  useEffect(() => {
    loadBalance(true);
  }, [loadBalance]);

  useEffect(() => {
    if (autoRefreshInterval > 0) {
      const interval = setInterval(() => {
        loadBalance(false);
      }, autoRefreshInterval);

      return () => clearInterval(interval);
    }
  }, [autoRefreshInterval, loadBalance]);

  const reload = useCallback(() => loadBalance(true), [loadBalance]);
  const refreshSilently = useCallback(() => loadBalance(false), [loadBalance]);

  return {
    balance,
    loading,
    error,
    lastUpdated,
    reload,
    refreshSilently
  };
}
export function useCurrentBalance(
  autoRefreshInterval: number = 300000,
  requestTimeout: number = DEFAULT_TIMEOUT
) {
  const { balance, loading, error, reload } = useBalance(autoRefreshInterval, requestTimeout);

  return {
    currentBalance: balance?.currentBalance || 0,
    availableBalance: balance?.availableBalance || 0,
    currency: balance?.currency || 'BRL',
    loading,
    error,
    reload,
    lastUpdated: balance?.lastUpdated
  };
}

export function hasSufficientBalance(paymentAmount: number, currentBalance: number): boolean {
  return currentBalance >= paymentAmount;
}

export function calculateBalanceImpact(currentBalance: number, paymentAmount: number) {
  const newBalance = currentBalance - paymentAmount;
  const isAllowed = newBalance >= 0;

  return {
    currentBalance,
    paymentAmount,
    newBalance,
    isAllowed,
    deficit: isAllowed ? 0 : Math.abs(newBalance)
  };
}